export const PENDING_OBSERVATION_TTL_MS = 2 * 60 * 1000
export const PENDING_OBSERVATION_MAX_MISSES = 4

const scopes = new Map()
let activeScopeKey = ''

export const canonicalBookingId = (booking) => String(booking?.bookingId || booking?.booking_id || booking?.id || '')
export const bookingCacheScopeKey = (scope = {}) => {
  const clubCode = String(scope?.clubCode || '').trim()
  const memberIdentity = String(scope?.memberIdentity || '').trim()
  return clubCode && memberIdentity ? `${clubCode}::${memberIdentity}` : ''
}

function stateFor(scopeKey) {
  if (!scopes.has(scopeKey)) scopes.set(scopeKey, { generation: 0, pending: new Map() })
  return scopes.get(scopeKey)
}

export function setActiveUpcomingBookingsScope(scope) {
  activeScopeKey = bookingCacheScopeKey(scope)
  if (activeScopeKey) stateFor(activeScopeKey)
  return activeScopeKey
}

export function clearUpcomingBookingsScope(scope) {
  const key = bookingCacheScopeKey(scope)
  scopes.delete(key)
  if (activeScopeKey === key) activeScopeKey = ''
}

export function currentUpcomingBookingsGeneration(scopeKey = activeScopeKey) {
  return scopeKey ? stateFor(scopeKey).generation : 0
}

export function normalizeMemberBooking(booking = {}) {
  if (!booking || typeof booking !== 'object' || Array.isArray(booking)) return null
  const bookingId = canonicalBookingId(booking)
  return { ...booking, bookingId, booking_id: undefined, id: booking.id || bookingId }
}

export function stripTransientBookingMetadata(bookings) {
  return (Array.isArray(bookings) ? bookings : []).flatMap((value) => {
    const normalized = normalizeMemberBooking(value)
    if (!normalized?.bookingId) return []
    const { _committedGeneration, _pendingAuthoritativeObservation, _committedAt, _observationMisses, ...booking } = value
    return [booking]
  })
}

export function mergeUpcomingBooking(bookings, booking) {
  const normalized = normalizeMemberBooking(booking)
  if (!normalized?.bookingId) return Array.isArray(bookings) ? bookings : []
  const existing = Array.isArray(bookings) ? bookings.map(normalizeMemberBooking).filter((item) => item?.bookingId) : []
  const found = existing.find((item) => item.bookingId === normalized.bookingId)
  const merged = found ? { ...found, ...normalized } : normalized
  return [merged, ...existing.filter((item) => item.bookingId !== normalized.bookingId)]
    .sort((a, b) => new Date(a.startAt || a.start_at || 8640000000000000) - new Date(b.startAt || b.start_at || 8640000000000000))
}

export function commitMemberBookingSuccess(booking, { clubCode, scopeKey = activeScopeKey, now = Date.now() } = {}) {
  const normalized = normalizeMemberBooking(booking)
  if (!normalized?.bookingId || !scopeKey) return false
  const activeClub = scopeKey.split('::')[0]
  if (clubCode && activeClub !== String(clubCode)) return false
  const state = stateFor(scopeKey)
  state.generation += 1
  const committed = { ...normalized, _committedGeneration: state.generation, _pendingAuthoritativeObservation: true, _committedAt: now, _observationMisses: 0 }
  state.pending.set(committed.bookingId, committed)
  window.dispatchEvent(new CustomEvent('members:booking-committed', { detail: { booking: committed, generation: state.generation, scopeKey } }))
  window.dispatchEvent(new CustomEvent('members:upcoming-bookings-invalidated', { detail: { scopeKey } }))
  console.info('[members-booking-cache] booking committed', { bookingId: committed.bookingId, module: committed.source || committed.module || committed.type, source: 'booking-success' })
  return committed
}

export function reconcileUpcomingBookings(authoritative, current, { scopeKey = activeScopeKey, now = Date.now() } = {}) {
  const normalizedAuthoritative = (Array.isArray(authoritative) ? authoritative : []).map(normalizeMemberBooking).filter((item) => item?.bookingId)
  // Identity is briefly incomplete during Auth0 restoration. Never create shared
  // state for that empty scope; the authoritative rows are still safe to show.
  if (!scopeKey) return normalizedAuthoritative
  const state = stateFor(scopeKey)
  const authoritativeById = new Map(normalizedAuthoritative.map((item) => [item.bookingId, item]))
  const currentById = new Map((Array.isArray(current) ? current : []).map(normalizeMemberBooking).filter((item) => item?.bookingId).map((item) => [item.bookingId, item]))

  for (const [id, pending] of state.pending) {
    if (authoritativeById.has(id)) {
      const authoritativeBooking = authoritativeById.get(id)
      const merged = { ...currentById.get(id), ...pending, ...authoritativeBooking }
      // A just-committed local projection may know media before a lagging read
      // model does. Null is not authoritative evidence that the image vanished.
      for (const field of ['imageUrl', 'imageFocusX', 'imageFocusY', 'imageFocalPosition']) {
        if (authoritativeBooking?.[field] == null && pending?.[field] != null) merged[field] = pending[field]
      }
      authoritativeById.set(id, { ...merged, _pendingAuthoritativeObservation: undefined, _committedAt: undefined, _committedGeneration: undefined, _observationMisses: undefined })
      state.pending.delete(id)
      continue
    }
    const misses = (pending._observationMisses || 0) + 1
    const expired = now - pending._committedAt > PENDING_OBSERVATION_TTL_MS || misses > PENDING_OBSERVATION_MAX_MISSES
    if (expired) state.pending.delete(id)
    else {
      const preserved = { ...currentById.get(id), ...pending, _observationMisses: misses }
      state.pending.set(id, preserved)
      authoritativeById.set(id, preserved)
    }
  }
  return [...authoritativeById.values()].sort((a, b) => new Date(a.startAt || a.start_at || 8640000000000000) - new Date(b.startAt || b.start_at || 8640000000000000))
}

export function createCommittedBooking({ id, module, title, location, startAt, timeZone, imageUrl, imageFocusX, imageFocusY, amount, currency, details = {} }) {
  return normalizeMemberBooking({ bookingId: id, source: module, type: module, title: title || `${module} booking`, locationName: location || title,
    startAt, timeZone, imageUrl, imageFocusX, imageFocusY, total_cost: amount, currency, status: 'confirmed', bookingReference: id, ...details })
}
